import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  CheckCircleOutline as ApproveIcon,
  Close as CloseIcon,
  CancelOutlined as CancelIcon,
  PauseCircleOutline as PauseIcon,
  PlayCircleOutline as ResumeIcon,
  Refresh as RefreshIcon,
  Replay as ReassignIcon,
  VisibilityOutlined as ViewIcon,
  Search as SearchIcon,
  Block as RejectIcon,
  AssignmentTurnedInOutlined as ReviewIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";

import CertificateService from "../../../services/certificateServices";


// =====================================================
// STATUS CONFIG
// =====================================================

const STATUS_CONFIG = {
  submitted: {
    label: "Submitted",
    color: "warning",
  },

  assigned: {
    label: "Assigned",
    color: "info",
  },

  paused: {
    label: "Paused",
    color: "default",
  },

  approved: {
    label: "Approved",
    color: "success",
  },

  cancelled: {
    label: "Cancelled",
    color: "error",
  },
};


// =====================================================
// HELPER
// =====================================================

const getErrorMessage = (error) => {
  return (
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    error?.response?.data?.detail ||
    error?.message ||
    "Something went wrong. Please try again."
  );
};


const formatDate = (value) => {
  if (!value) return "-";

  try {
    return new Date(value).toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return value;
  }
};


const getJobId = (job) => {
  return (
    job?.job_id ??
    job?.id ??
    job?.pk ??
    "-"
  );
};


const getAssignedAgent = (job) => {
  return (
    job?.assigned_agent?.name ||
    job?.assigned_agent?.username ||
    job?.assigned_agent?.email ||
    job?.assigned_agent_name ||
    job?.assigned_to ||
    "-"
  );
};


const getVehicleNumber = (job) => {
  return (
    job?.imei ||
    "-"
  );
};

const getFileUrl = (file, type = "certificate") => {
  if (!file) return "";

  const filePath =
    typeof file === "string"
      ? file
      : file?.file_path || file?.url || file?.path || "";

  if (!filePath || typeof filePath !== "string") {
    return "";
  }

  // Already a complete URL
  if (
    filePath.startsWith("http://") ||
    filePath.startsWith("https://")
  ) {
    return filePath;
  }

  const baseUrl = process.env.REACT_APP_BASE_URL;

  if (!baseUrl) {
    return "";
  }

  const cleanPath = filePath
    .replace(/^https?:\/\/[^/]+/, "")
    .replace(/^\/+/, "");

  // Certificate files
  if (type === "certificate") {
    const certificatePath = cleanPath
      .replace(/^fileuploads\/certificates\//, "")
      .replace(/^files\/certificates\//, "");

    return `${baseUrl.replace(/\/$/, "")}/files/certificates/${certificatePath}`;
  }

  // Technician installation files
  if (type === "technician") {
    const technicianPath = cleanPath
      .replace(/^fileuploads\/technician_install\//, "")
      .replace(/^files\/technician_install\//, "");

    return `${baseUrl.replace(/\/$/, "")}/files/technician_install/${technicianPath}`;
  }

  return "";
};

// =====================================================
// MAIN COMPONENT
// =====================================================

const CertificateReviewQueue = () => {

  // ---------------------------------------------------
  // USER / ROLE
  // ---------------------------------------------------

  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);


  const role = String(
    user?.role ||
    localStorage.getItem("role") ||
    ""
  ).toLowerCase();


  const isSuperAdmin = role === "superadmin";
  const isCertificateAdmin = role === "certificateadmin";
  const isTeamLead = role === "certificateteamlead";

  const canAdminActions =
    isSuperAdmin || isCertificateAdmin;


  // ---------------------------------------------------
  // STATE
  // ---------------------------------------------------

  const [jobs, setJobs] = useState([]);

  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");

  const [statusFilter, setStatusFilter] =
    useState("submitted");


  const [selectedJob, setSelectedJob] = useState(null);
const [detailLoading, setDetailLoading] = useState(false);


  // Dialog
  const [dialog, setDialog] = useState({
    open: false,
    type: null,
    job: null,
  });


  // Reject
  const [rejectReason, setRejectReason] =
    useState("");

  const [rejectAgentId, setRejectAgentId] =
    useState("");


  // Reassign
  const [reassignAgentId, setReassignAgentId] =
    useState("");


  // Cancel
  const [cancelReason, setCancelReason] =
    useState("");


  // Agents
  const [agents, setAgents] =
    useState([]);


  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });


  // ---------------------------------------------------
  // SNACKBAR
  // ---------------------------------------------------

  const showSnackbar = useCallback(
    (message, severity = "success") => {
      setSnackbar({
        open: true,
        message,
        severity,
      });
    },
    []
  );


  const closeSnackbar = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  };


  // ---------------------------------------------------
  // LOAD REVIEW QUEUE
  // ---------------------------------------------------

  const loadReviewQueue = useCallback(async () => {

    setLoading(true);

    try {

      const params = {};

      if (statusFilter) {
        params.status = statusFilter;
      }

      const response =
        await CertificateService.getCertificateReviewQueue(
          params
        );


      const responseData = response?.data;


      const list =
        Array.isArray(responseData)
          ? responseData
          : responseData?.results ||
            responseData?.data ||
            responseData?.jobs ||
            [];


      setJobs(list);

    } catch (error) {

      showSnackbar(
        getErrorMessage(error),
        "error"
      );

    } finally {

      setLoading(false);

    }

  }, [statusFilter, showSnackbar]);


  useEffect(() => {
    loadReviewQueue();
  }, [loadReviewQueue]);


  // ---------------------------------------------------
  // LOAD AGENTS
  // ---------------------------------------------------

  const loadAgents = useCallback(async () => {

    if (!canAdminActions) {
      return;
    }

    try {

      const response =
        await CertificateService.getCertificateExecutiveList({
          page: 1,
          page_size: 100,
        });


      const data = response?.data;

      const list =
        Array.isArray(data)
          ? data
          : data?.results ||
            data?.data ||
            data?.users ||
            [];


      setAgents(list);

    } catch (error) {

      showSnackbar(
        getErrorMessage(error),
        "error"
      );

    }

  }, [canAdminActions, showSnackbar]);


  useEffect(() => {
    loadAgents();
  }, [loadAgents]);


  // ---------------------------------------------------
  // SEARCH
  // ---------------------------------------------------

  const filteredJobs = useMemo(() => {

    const query =
      search.trim().toLowerCase();


    if (!query) {
      return jobs;
    }


    return jobs.filter((job) => {

      const searchable = [
        getJobId(job),
        getVehicleNumber(job),
        getAssignedAgent(job),
        job?.state_name,
        job?.district_name,
        job?.manufacturer_name,
        job?.protocol_format,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();


      return searchable.includes(query);

    });

  }, [jobs, search]);


  // ===================================================
  // DIALOG HELPERS
  // ===================================================

  const openDialog = (type, job) => {

    setRejectReason("");
    setRejectAgentId("");
    setReassignAgentId("");
    setCancelReason("");

    setDialog({
      open: true,
      type,
      job,
    });
  };


  const closeDialog = () => {

    setDialog({
      open: false,
      type: null,
      job: null,
    });

    setRejectReason("");
    setRejectAgentId("");
    setReassignAgentId("");
    setCancelReason("");
  };

const handleViewDetails = async (job) => {
  const jobId = getJobId(job);

  if (!jobId || jobId === "-") {
    showSnackbar("Invalid job ID.", "error");
    return;
  }

  setDetailLoading(true);

  // Open the same details dialog immediately
  setDialog({
    open: true,
    type: "details",
    job,
  });

  try {
    const response =
      await CertificateService.getCertificateJobDetail(jobId);

    const detailData = response?.data;

    if (!detailData) {
      showSnackbar(
        "No job detail data found.",
        "error"
      );
      return;
    }

    setSelectedJob(detailData);

    // Store the complete detail API response in dialog.job
    setDialog((prev) => ({
      ...prev,
      job: detailData,
    }));
  } catch (error) {
    showSnackbar(
      getErrorMessage(error),
      "error"
    );
  } finally {
    setDetailLoading(false);
  }
};


  // ===================================================
  // APPROVE
  // ===================================================

  const handleApprove = async () => {

    const job = dialog.job;

    if (!job) return;


    setLoading(true);

    try {

      await CertificateService.approveCertificateJob({
        job_id: getJobId(job),
      });


      showSnackbar(
        "Certificate job approved successfully.",
        "success"
      );

      closeDialog();

      await loadReviewQueue();

    } catch (error) {

      showSnackbar(
        getErrorMessage(error),
        "error"
      );

    } finally {

      setLoading(false);

    }
  };


  // ===================================================
  // REJECT
  // ===================================================

  const handleReject = async () => {

    const job = dialog.job;

    if (!job) return;


    if (!rejectReason.trim()) {

      showSnackbar(
        "Reject reason is required.",
        "error"
      );

      return;
    }


    setLoading(true);

    try {

      const payload = {
        job_id: getJobId(job),
        reason: rejectReason.trim(),
      };


      if (rejectAgentId) {
        payload.reassign_to_agent_id =
          rejectAgentId;
      }


      await CertificateService.rejectCertificateJob(
        payload
      );


      showSnackbar(
        "Certificate job rejected successfully.",
        "success"
      );


      closeDialog();

      await loadReviewQueue();

    } catch (error) {

      showSnackbar(
        getErrorMessage(error),
        "error"
      );

    } finally {

      setLoading(false);

    }
  };


  // ===================================================
  // REASSIGN
  // ===================================================

  const handleReassign = async () => {

    const job = dialog.job;

    if (!job) return;


    if (!reassignAgentId) {

      showSnackbar(
        "Please select an agent.",
        "error"
      );

      return;
    }


    setLoading(true);

    try {

      await CertificateService.reassignCertificateJob({
        job_id: getJobId(job),
        agent_id: reassignAgentId,
      });


      showSnackbar(
        "Job reassigned successfully.",
        "success"
      );


      closeDialog();

      await loadReviewQueue();

    } catch (error) {

      showSnackbar(
        getErrorMessage(error),
        "error"
      );

    } finally {

      setLoading(false);

    }
  };


  // ===================================================
  // CANCEL
  // ===================================================

  const handleCancel = async () => {

    const job = dialog.job;

    if (!job) return;


    if (!cancelReason.trim()) {

      showSnackbar(
        "Cancellation reason is required.",
        "error"
      );

      return;
    }


    setLoading(true);

    try {

      await CertificateService.cancelCertificateJob({
        job_id: getJobId(job),
        reason: cancelReason.trim(),
      });


      showSnackbar(
        "Certificate job cancelled successfully.",
        "success"
      );


      closeDialog();

      await loadReviewQueue();

    } catch (error) {

      showSnackbar(
        getErrorMessage(error),
        "error"
      );

    } finally {

      setLoading(false);

    }
  };


  // ===================================================
  // PAUSE
  // ===================================================

  const handlePause = async () => {

    const job = dialog.job;

    if (!job) return;


    setLoading(true);

    try {

      await CertificateService.pauseCertificateJob({
        job_id: getJobId(job),
      });


      showSnackbar(
        "Job paused successfully.",
        "success"
      );


      closeDialog();

      await loadReviewQueue();

    } catch (error) {

      showSnackbar(
        getErrorMessage(error),
        "error"
      );

    } finally {

      setLoading(false);

    }
  };


  // ===================================================
  // RESUME
  // ===================================================

  const handleResume = async () => {

    const job = dialog.job;

    if (!job) return;


    setLoading(true);

    try {

      await CertificateService.resumeCertificateJob({
        job_id: getJobId(job),
      });


      showSnackbar(
        "Job resumed successfully.",
        "success"
      );


      closeDialog();

      await loadReviewQueue();

    } catch (error) {

      showSnackbar(
        getErrorMessage(error),
        "error"
      );

    } finally {

      setLoading(false);

    }
  };


  // ===================================================
  // DIALOG CONTENT
  // ===================================================

  const renderDialogContent = () => {

    const job = dialog.job;

    if (!job) return null;


    switch (dialog.type) {

      // -----------------------------------------------
      // DETAILS
      // -----------------------------------------------

     case "details": {
  const detail = job;
  const jobData = detail?.job || {};
  const vehicle = detail?.vehicle || {};
  const owner = detail?.owner || {};
  const dealer = detail?.dealer || {};
  const manufacturer = detail?.manufacturer || {};
  const device = detail?.device || {};
  const installation = detail?.installation || {};
  const photoLocationFields = {
  install_photo: {
    label: "Install Photo Lat Lon",
    lat: installation?.install_photo_lat,
    lon: installation?.install_photo_lon,
  },
  vehicle_front_photo: {
    label: "Vehicle Front Photo Lat Lon",
    lat: installation?.vehicle_front_photo_lat,
    lon: installation?.vehicle_front_photo_lon,
  },
  panic_button_photo: {
    label: "Panic Button Photo Lat Lon",
    lat: installation?.panic_button_photo_lat,
    lon: installation?.panic_button_photo_lon,
  },
};
  const driverContacts = Array.isArray(detail?.driver_contacts)
    ? detail.driver_contacts
    : [];

 

 const getFileName = (file, index) => {
  if (!file) {
    return `Document ${index + 1}`;
  }

  if (typeof file === "string") {
    return (
      file.split("/").pop() ||
      `Document ${index + 1}`
    );
  }

  return (
    file?.label ||
    file?.name ||
    file?.file_name ||
    file?.filename ||
    file?.original_name ||
    file?.file_path?.split("/").pop() ||
    `Document ${index + 1}`
  );
};

const uploadedFiles = Array.isArray(jobData?.files)
  ? jobData.files
  : [];

const uniqueFiles = uploadedFiles.filter(
  (file, index, array) => {
    const currentUrl = getFileUrl(file);
    const currentName = getFileName(file, index);

    return (
      array.findIndex((item, itemIndex) => {
        const itemUrl = getFileUrl(item);
        const itemName = getFileName(item, itemIndex);

        if (currentUrl && itemUrl) {
          return currentUrl === itemUrl;
        }

        return currentName === itemName;
      }) === index
    );
  }
);

  
  return (
    <>
      <DialogTitle>
        Certificate Job Details
      </DialogTitle>

      <DialogContent dividers>
        {detailLoading ? (
          <Box
            sx={{
              minHeight: 300,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
            }}
          >
            <CircularProgress />

            <Typography
              variant="body2"
              color="text.secondary"
            >
              Loading job details...
            </Typography>
          </Box>
        ) : (
          <Stack spacing={3}>

            {/* =================================================
                JOB INFORMATION
            ================================================= */}

            <Box>
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{ mb: 2 }}
              >
                Job Information
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <DetailItem
                    label="Job ID"
                    value={
                      jobData?.job_id ||
                      jobData?.id ||
                      jobData?.pk ||
                      getJobId(detail)
                    }
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <DetailItem
                    label="Status"
                    value={jobData?.status}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <DetailItem
                    label="Protocol"
                    value={
                      jobData?.protocol_format ||
                      jobData?.protocol
                    }
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <DetailItem
                    label="Assigned Agent"
                    value={
                      jobData?.assigned_agent?.name ||
                      jobData?.assigned_agent?.username ||
                      jobData?.assigned_agent?.email ||
                      jobData?.assigned_agent_name ||
                      jobData?.assigned_to
                    }
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <DetailItem
                    label="Created"
                    value={formatDate(jobData?.created)}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <DetailItem
                    label="Submitted At"
                    value={formatDate(jobData?.submitted_at)}
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            {/* =================================================
                VEHICLE
            ================================================= */}

            <Box>
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{ mb: 2 }}
              >
                Vehicle Details
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <DetailItem
                    label="Registration Number"
                    value={vehicle?.reg_no}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <DetailItem
                    label="Chassis Number"
                    value={vehicle?.chassis_no}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <DetailItem
                    label="Engine Number"
                    value={vehicle?.engine_no}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <DetailItem
                    label="Make"
                    value={vehicle?.make}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <DetailItem
                    label="Model"
                    value={vehicle?.model}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <DetailItem
                    label="Vehicle Status"
                    value={vehicle?.status}
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            {/* =================================================
                OWNER
            ================================================= */}

            <Box>
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{ mb: 2 }}
              >
                Owner Details
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <DetailItem
                    label="Owner ID"
                    value={owner?.id}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <DetailItem
                    label="Company Name"
                    value={owner?.company_name}
                  />
                </Grid>
              </Grid>

              {Array.isArray(owner?.contacts) &&
                owner.contacts.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      sx={{ mb: 1 }}
                    >
                      Owner Contacts
                    </Typography>

                    <Stack spacing={1}>
                      {owner.contacts.map(
                        (contact, index) => (
                          <Paper
                            key={
                              contact?.id ||
                              `owner-contact-${index}`
                            }
                            variant="outlined"
                            sx={{ p: 1.5 }}
                          >
                            <Grid
                              container
                              spacing={1}
                            >
                              <Grid
                                item
                                xs={12}
                                sm={4}
                              >
                                <DetailItem
                                  label="Name"
                                  value={
                                    contact?.name
                                  }
                                />
                              </Grid>

                              <Grid
                                item
                                xs={12}
                                sm={4}
                              >
                                <DetailItem
                                  label="Mobile"
                                  value={
                                    contact?.mobile
                                  }
                                />
                              </Grid>

                              <Grid
                                item
                                xs={12}
                                sm={4}
                              >
                                <DetailItem
                                  label="Email"
                                  value={
                                    contact?.email
                                  }
                                />
                              </Grid>
                            </Grid>
                          </Paper>
                        )
                      )}
                    </Stack>
                  </Box>
                )}
            </Box>

            <Divider />

            {/* =================================================
                DEALER / MANUFACTURER
            ================================================= */}

            <Box>
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{ mb: 2 }}
              >
                Dealer & Manufacturer
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <DetailItem
                    label="Dealer"
                    value={dealer?.company_name}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <DetailItem
                    label="Manufacturer"
                    value={manufacturer?.company_name}
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            {/* =================================================
                DEVICE
            ================================================= */}

            <Box>
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{ mb: 2 }}
              >
                Device Details
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <DetailItem
                    label="Device ID"
                    value={device?.id}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <DetailItem
                    label="Device ESN"
                    value={device?.device_esn}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <DetailItem
                    label="IMEI"
                    value={device?.imei}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <DetailItem
                    label="ICCID"
                    value={device?.iccid}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <DetailItem
                    label="FOTA Vendor"
                    value={device?.fota_vendor}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <DetailItem
                    label="FOTA Version"
                    value={device?.fota_version}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <DetailItem
                    label="FOTA Protocol"
                    value={
                      device?.fota_protocol_format
                    }
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <DetailItem
                    label="FOTA Status"
                    value={device?.fota_status}
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            {/* =================================================
                INSTALLATION
            ================================================= */}
            <Box>
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{ mb: 2 }}
              >
                Installation Details
              </Typography>

              <Grid container spacing={2}>
               {Object.entries(installation).map(([key, value]) => {
  // -------------------------------------------------
  // Photo fields
  // -------------------------------------------------
  const installationPhotoFields = [
    "install_photo",
    "vehicle_front_photo",
    "panic_button_photo",
  ];

  // -------------------------------------------------
  // Hide individual latitude / longitude fields
  // because we will display them below the photo
  // -------------------------------------------------
  const hiddenLocationFields = [
    "install_photo_lat",
    "install_photo_lon",
    "vehicle_front_photo_lat",
    "vehicle_front_photo_lon",
    "panic_button_photo_lat",
    "panic_button_photo_lon",
  ];

  if (hiddenLocationFields.includes(key)) {
    return null;
  }

  // -------------------------------------------------
  // Installation photos
  // -------------------------------------------------
  if (installationPhotoFields.includes(key)) {
    const photoUrl = getFileUrl(value, "technician");

    const photoLabels = {
      install_photo: "Install Photo",
      vehicle_front_photo: "Vehicle Front Photo",
      panic_button_photo: "Panic Button Photo",
    };

    const location = photoLocationFields[key];

    return (
    <React.Fragment key={key}>
      {/* =========================
          PHOTO - LEFT COLUMN
      ========================= */}
      <Grid item xs={12} sm={6}>
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            borderRadius: 2,
            minHeight: 70,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography
            variant="body2"
            fontWeight={700}
          >
            {photoLabels[key]}
          </Typography>

          {photoUrl ? (
            <Tooltip title={`View ${photoLabels[key]}`}>
              <IconButton
                size="small"
                color="primary"
                component="a"
                href={photoUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(event) => {
                  event.stopPropagation();
                }}
              >
                <ViewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <Typography
              variant="caption"
              color="error"
            >
              File unavailable
            </Typography>
          )}
        </Paper>
      </Grid>

      {/* =========================
          LAT/LON - RIGHT COLUMN
      ========================= */}
      <Grid item xs={12} sm={6}>
        <Box
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            pl: 1,
          }}
        >
          <Typography
            variant="body2"
            fontWeight={700}
          >
            {location?.label}
          </Typography>

          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ mt: 0.75 }}
          >
            {location?.lat ?? "-"}, {location?.lon ?? "-"}
          </Typography>
        </Box>
      </Grid>
    </React.Fragment>
  );
  }

  // -------------------------------------------------
  // Don't display uploaded file collections
  // -------------------------------------------------
  if (
    [
      "certificate_files",
      "uploaded_files",
      "files",
      "documents",
    ].includes(key)
  ) {
    return null;
  }

  // -------------------------------------------------
  // Don't display objects
  // -------------------------------------------------
  if (
    typeof value === "object" &&
    value !== null
  ) {
    return null;
  }

  // -------------------------------------------------
  // Normal installation fields
  // -------------------------------------------------
  return (
    <Grid
      item
      xs={12}
      sm={6}
      key={key}
    >
      <DetailItem
        label={key
          .replace(/_/g, " ")
          .replace(/\b\w/g, (char) =>
            char.toUpperCase()
          )}
        value={
          typeof value === "boolean"
            ? value
              ? "Yes"
              : "No"
            : value
        }
      />
    </Grid>
  );
})}
              </Grid>
            </Box>

            <Divider />

            {/* =================================================
                DRIVER CONTACTS
            ================================================= */}

            <Box>
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{ mb: 2 }}
              >
                Driver Contacts
              </Typography>

              {driverContacts.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  No driver contacts available.
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {driverContacts.map(
                    (contact, index) => (
                      <Paper
                        key={
                          contact?.id ||
                          `driver-${index}`
                        }
                        variant="outlined"
                        sx={{ p: 1.5 }}
                      >
                        <Grid container spacing={1}>
                          <Grid
                            item
                            xs={12}
                            sm={6}
                          >
                            <DetailItem
                              label="Name"
                              value={contact?.name}
                            />
                          </Grid>

                          <Grid
                            item
                            xs={12}
                            sm={6}
                          >
                            <DetailItem
                              label="Phone"
                              value={
                                contact?.phone_no
                              }
                            />
                          </Grid>
                        </Grid>
                      </Paper>
                    )
                  )}
                </Stack>
              )}
            </Box>

            <Divider />

            {/* =================================================
                UPLOADED FILES
            ================================================= */}

          

<Box>
  <Typography
    variant="h6"
    fontWeight={700}
    sx={{ mb: 2 }}
  >
    Uploaded Files
  </Typography>

  {uniqueFiles.length === 0 ? (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        textAlign: "center",
      }}
    >
      <Typography
        variant="body2"
        color="text.secondary"
      >
        No uploaded files available.
      </Typography>
    </Paper>
  ) : (
    <Stack spacing={1}>
      {uniqueFiles.map((file, index) => {
        const fileUrl = getFileUrl(
          file?.file_path
        );

        const fileName =
          file?.label ||
          file?.file_path?.split("/").pop() ||
          `Document ${index + 1}`;

        return (
          <Paper
            key={
              file?.id ||
              `${fileName}-${index}`
            }
            variant="outlined"
            sx={{
              p: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Box
              sx={{
                minWidth: 0,
                flex: 1,
              }}
            >
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={fileName}
              >
                {fileName}
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
              >
                Uploaded by:{" "}
                {file?.uploaded_by_name || "-"}
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Uploaded at:{" "}
                {formatDate(file?.uploaded_at)}
              </Typography>
            </Box>

            {fileUrl ? (
              <Tooltip title="View File">
                <IconButton
                  size="small"
                  color="primary"
                  component="a"
                  href={fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(event) => {
                    event.stopPropagation();
                  }}
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : (
              <Typography
                variant="caption"
                color="error"
              >
                File unavailable
              </Typography>
            )}
          </Paper>
        );
      })}
    </Stack>
  )}
</Box>

            {/* Reject reason */}
            {jobData?.reject_reason && (
              <>
                <Divider />

                <Alert severity="error">
                  <strong>
                    Reject Reason:
                  </strong>{" "}
                  {jobData.reject_reason}
                </Alert>
              </>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          onClick={() => {
            setSelectedJob(null);
            closeDialog();
          }}
          variant="contained"
        >
          Close
        </Button>
      </DialogActions>
    </>
  );
}

      // -----------------------------------------------
      // APPROVE
      // -----------------------------------------------

      case "approve":

        return (
          <>
            <DialogTitle>
              Approve Certificate Job
            </DialogTitle>

            <DialogContent dividers>

              <Typography>
                Are you sure you want to approve job{" "}
                <strong>#{getJobId(job)}</strong>?
              </Typography>

              <Alert
                severity="success"
                sx={{ mt: 2 }}
              >
                This will change the job status to
                <strong> approved</strong>.
              </Alert>

            </DialogContent>

            <DialogActions>

              <Button
                onClick={closeDialog}
                disabled={loading}
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                color="success"
                startIcon={<ApproveIcon />}
                onClick={handleApprove}
                disabled={loading}
              >
                {loading ? (
                  <CircularProgress size={20} />
                ) : (
                  "Approve"
                )}
              </Button>

            </DialogActions>
          </>
        );


      // -----------------------------------------------
      // REJECT
      // -----------------------------------------------

      case "reject":

        return (
          <>
            <DialogTitle>
              Reject Certificate Job
            </DialogTitle>

            <DialogContent dividers>

              <Alert severity="warning" sx={{ mb: 2 }}>
                Rejecting this job will move it back to
                <strong> assigned</strong> status and
                previously uploaded certificate files will
                be deleted.
              </Alert>

              <TextField
                fullWidth
                required
                multiline
                minRows={4}
                label="Reject Reason"
                placeholder="Enter reason for rejection..."
                value={rejectReason}
                onChange={(event) =>
                  setRejectReason(event.target.value)
                }
                sx={{ mb: 2 }}
              />

              <FormControl fullWidth>
                <InputLabel>
                  Reassign To Agent (Optional)
                </InputLabel>

                <Select
                  label="Reassign To Agent (Optional)"
                  value={rejectAgentId}
                  onChange={(event) =>
                    setRejectAgentId(event.target.value)
                  }
                >
                  <MenuItem value="">
                    Same Agent
                  </MenuItem>

                  {agents.map((agent) => (
                    <MenuItem
                      key={
                        agent.id ||
                        agent.user_id
                      }
                      value={
                        agent.id ||
                        agent.user_id
                      }
                    >
                      {agent.name ||
                        agent.username ||
                        agent.email ||
                        `Agent #${agent.id}`}
                    </MenuItem>
                  ))}

                </Select>
              </FormControl>

            </DialogContent>

            <DialogActions>

              <Button
                onClick={closeDialog}
                disabled={loading}
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                color="error"
                startIcon={<RejectIcon />}
                onClick={handleReject}
                disabled={loading}
              >
                {loading ? (
                  <CircularProgress size={20} />
                ) : (
                  "Reject Job"
                )}
              </Button>

            </DialogActions>
          </>
        );


      // -----------------------------------------------
      // REASSIGN
      // -----------------------------------------------

      case "reassign":

        return (
          <>
            <DialogTitle>
              Reassign Certificate Job
            </DialogTitle>

            <DialogContent dividers>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2 }}
              >
                Select the certificate executive/team lead
                who should receive this job.
              </Typography>

              <FormControl fullWidth>

                <InputLabel>
                  Assign To
                </InputLabel>

                <Select
                  label="Assign To"
                  value={reassignAgentId}
                  onChange={(event) =>
                    setReassignAgentId(event.target.value)
                  }
                >

                  <MenuItem value="">
                    Select agent
                  </MenuItem>

                  {agents.map((agent) => (

                    <MenuItem
                      key={
                        agent.id ||
                        agent.user_id
                      }
                      value={
                        agent.id ||
                        agent.user_id
                      }
                    >
                      {agent.name ||
                        agent.username ||
                        agent.email ||
                        `Agent #${agent.id}`}
                    </MenuItem>

                  ))}

                </Select>

              </FormControl>

            </DialogContent>

            <DialogActions>

              <Button
                onClick={closeDialog}
                disabled={loading}
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                startIcon={<ReassignIcon />}
                onClick={handleReassign}
                disabled={loading}
              >
                {loading ? (
                  <CircularProgress size={20} />
                ) : (
                  "Reassign"
                )}
              </Button>

            </DialogActions>
          </>
        );


      // -----------------------------------------------
      // CANCEL
      // -----------------------------------------------

      case "cancel":

        return (
          <>
            <DialogTitle>
              Cancel Certificate Job
            </DialogTitle>

            <DialogContent dividers>

              <Alert severity="error" sx={{ mb: 2 }}>
                This action cannot be performed after the
                job has been approved or already cancelled.
              </Alert>

              <TextField
                fullWidth
                required
                multiline
                minRows={4}
                label="Cancellation Reason"
                placeholder="Enter cancellation reason..."
                value={cancelReason}
                onChange={(event) =>
                  setCancelReason(event.target.value)
                }
              />

            </DialogContent>

            <DialogActions>

              <Button
                onClick={closeDialog}
                disabled={loading}
              >
                Keep Job
              </Button>

              <Button
                variant="contained"
                color="error"
                startIcon={<CancelIcon />}
                onClick={handleCancel}
                disabled={loading}
              >
                {loading ? (
                  <CircularProgress size={20} />
                ) : (
                  "Cancel Job"
                )}
              </Button>

            </DialogActions>
          </>
        );


      // -----------------------------------------------
      // PAUSE
      // -----------------------------------------------

      case "pause":

        return (
          <>
            <DialogTitle>
              Pause Certificate Job
            </DialogTitle>

            <DialogContent dividers>

              <Typography>
                Are you sure you want to pause job{" "}
                <strong>#{getJobId(job)}</strong>?
              </Typography>

            </DialogContent>

            <DialogActions>

              <Button
                onClick={closeDialog}
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                startIcon={<PauseIcon />}
                onClick={handlePause}
                disabled={loading}
              >
                Pause Job
              </Button>

            </DialogActions>
          </>
        );


      // -----------------------------------------------
      // RESUME
      // -----------------------------------------------

      case "resume":

        return (
          <>
            <DialogTitle>
              Resume Certificate Job
            </DialogTitle>

            <DialogContent dividers>

              <Typography>
                Resume job{" "}
                <strong>#{getJobId(job)}</strong>?
              </Typography>

            </DialogContent>

            <DialogActions>

              <Button
                onClick={closeDialog}
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                color="success"
                startIcon={<ResumeIcon />}
                onClick={handleResume}
                disabled={loading}
              >
                Resume Job
              </Button>

            </DialogActions>
          </>
        );


      default:
        return null;
    }
  };


  // ===================================================
  // ACTIONS
  // ===================================================

  const renderActions = (job) => {

    const status = String(
      job?.status || ""
    ).toLowerCase();


    return (
      <Stack
        direction="row"
        spacing={0.5}
        justifyContent="flex-end"
      >

        {/* Details */}

        <Tooltip title="View Details">
  <IconButton
    size="small"
    onClick={() => handleViewDetails(job)}
  >
    <ViewIcon fontSize="small" />
  </IconButton>
</Tooltip>


        {/* Submitted */}

        {status === "submitted" && (

          <>

            <Tooltip title="Approve">

              <IconButton
                size="small"
                color="success"
                onClick={() =>
                  openDialog("approve", job)
                }
              >
                <ApproveIcon fontSize="small" />
              </IconButton>

            </Tooltip>


            <Tooltip title="Reject">

              <IconButton
                size="small"
                color="error"
                onClick={() =>
                  openDialog("reject", job)
                }
              >
                <RejectIcon fontSize="small" />
              </IconButton>

            </Tooltip>

          </>
        )}


        {/* Admin / Superadmin */}

        {canAdminActions && (
          <>

            {status !== "approved" &&
              status !== "cancelled" && (

                <Tooltip title="Reassign">

                  <IconButton
                    size="small"
                    onClick={() =>
                      openDialog(
                        "reassign",
                        job
                      )
                    }
                  >
                    <ReassignIcon fontSize="small" />
                  </IconButton>

                </Tooltip>
              )}


            {status === "assigned" && (

              <Tooltip title="Pause">

                <IconButton
                  size="small"
                  onClick={() =>
                    openDialog(
                      "pause",
                      job
                    )
                  }
                >
                  <PauseIcon fontSize="small" />
                </IconButton>

              </Tooltip>
            )}


            {status === "paused" && (

              <Tooltip title="Resume">

                <IconButton
                  size="small"
                  color="success"
                  onClick={() =>
                    openDialog(
                      "resume",
                      job
                    )
                  }
                >
                  <ResumeIcon fontSize="small" />
                </IconButton>

              </Tooltip>
            )}


            {status !== "approved" &&
              status !== "cancelled" && (

                <Tooltip title="Cancel Job">

                  <IconButton
                    size="small"
                    color="error"
                    onClick={() =>
                      openDialog(
                        "cancel",
                        job
                      )
                    }
                  >
                    <CancelIcon fontSize="small" />
                  </IconButton>

                </Tooltip>
              )}

          </>
        )}

      </Stack>
    );
  };


  // ===================================================
  // TABLE
  // ===================================================

  return (
    <Box
      sx={{
        p: {
          xs: 2,
          md: 3,
        },

        backgroundColor: "#f6f8fb",
        minHeight: "100vh",
      }}
    >

      {/* =================================================
          HEADER
      ================================================= */}

      <Box
        sx={{
          mb: 3,
          display: "flex",
          alignItems: {
            xs: "flex-start",
            md: "center",
          },
          justifyContent: "space-between",
          flexDirection: {
            xs: "column",
            md: "row",
          },
          gap: 2,
        }}
      >

        <Box>

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
          >

            <ReviewIcon
              sx={{
                fontSize: 30,
              }}
            />

            <Typography
              variant="h5"
              fontWeight={700}
            >
              Certificate Review Queue
            </Typography>

          </Stack>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.5 }}
          >
            Review and manage submitted certificate jobs.
          </Typography>

        </Box>


        <Button
          variant="outlined"
          startIcon={
            loading ? (
              <CircularProgress size={18} />
            ) : (
              <RefreshIcon />
            )
          }
          onClick={loadReviewQueue}
          disabled={loading}
        >
          Refresh
        </Button>

      </Box>


      {/* =================================================
          SUMMARY
      ================================================= */}

      <Grid
        container
        spacing={2}
        sx={{ mb: 3 }}
      >

        <Grid item xs={12} sm={6} md={3}>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
            }}
          >

            <Typography
              variant="body2"
              color="text.secondary"
            >
              Review Queue
            </Typography>

            <Typography
              variant="h4"
              fontWeight={700}
              sx={{ mt: 0.5 }}
            >
              {jobs.length}
            </Typography>

          </Paper>

        </Grid>


        <Grid item xs={12} sm={6} md={3}>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
            }}
          >

            <Typography
              variant="body2"
              color="text.secondary"
            >
              Submitted
            </Typography>

            <Typography
              variant="h4"
              fontWeight={700}
              sx={{ mt: 0.5 }}
            >
              {
                jobs.filter(
                  (job) =>
                    job?.status ===
                    "submitted"
                ).length
              }
            </Typography>

          </Paper>

        </Grid>

      </Grid>


      {/* =================================================
          FILTERS
      ================================================= */}

      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
        }}
      >

        <Grid
          container
          spacing={2}
          alignItems="center"
        >

          <Grid item xs={12} md={7}>

            <TextField
              fullWidth
              size="small"
              placeholder="Search by Job ID, vehicle, agent, state, district..."
              value={search}
              onChange={(event) =>
                setSearch(event.target.value)
              }
              InputProps={{
                startAdornment: (
                  <SearchIcon
                    sx={{
                      mr: 1,
                      color: "text.secondary",
                    }}
                  />
                ),
              }}
            />

          </Grid>


          <Grid item xs={12} md={3}>

            <FormControl
              fullWidth
              size="small"
            >

              <InputLabel>
                Status
              </InputLabel>

              <Select
                label="Status"
                value={statusFilter}
                onChange={(event) =>
                  setStatusFilter(
                    event.target.value
                  )
                }
              >

                <MenuItem value="submitted">
                  Submitted
                </MenuItem>

                <MenuItem value="">
                  All
                </MenuItem>

              </Select>

            </FormControl>

          </Grid>

        </Grid>

      </Paper>


      {/* =================================================
          TABLE
      ================================================= */}

      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          overflow: "hidden",
        }}
      >

        <Box
          sx={{
            overflowX: "auto",
          }}
        >

          <Box
            component="table"
            sx={{
              width: "100%",
              borderCollapse: "collapse",
              minWidth: 1100,

              "& th": {
                backgroundColor: "#f8fafc",
                textAlign: "left",
                fontSize: 13,
                fontWeight: 700,
                color: "text.secondary",
                padding: "14px 16px",
                borderBottom: "1px solid",
                borderColor: "divider",
                whiteSpace: "nowrap",
              },

              "& td": {
                padding: "14px 16px",
                borderBottom: "1px solid",
                borderColor: "divider",
                fontSize: 14,
              },

              "& tbody tr:hover": {
                backgroundColor: "#fafafa",
              },
            }}
          >

            <thead>

              <tr>

                <th>Job ID</th>

                <th>Imei</th>

                <th>Assigned Agent</th>

                <th>Manufacturer</th>

                <th>Protocol</th>

                <th>State</th>

                <th>District</th>

                <th>Submitted At</th>

                <th>Status</th>

                <th>
                  <Box
                    sx={{
                      textAlign: "right",
                    }}
                  >
                    Actions
                  </Box>
                </th>

              </tr>

            </thead>


            <tbody>

              {loading && jobs.length === 0 ? (

                <tr>

                  <td
                    colSpan={10}
                    style={{
                      textAlign: "center",
                      padding: 50,
                    }}
                  >

                    <CircularProgress />

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      Loading review queue...
                    </Typography>

                  </td>

                </tr>

              ) : filteredJobs.length === 0 ? (

                <tr>

                  <td
                    colSpan={10}
                    style={{
                      textAlign: "center",
                      padding: 50,
                    }}
                  >

                    <Typography
                      variant="h6"
                      fontWeight={600}
                    >
                      No jobs found
                    </Typography>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.5 }}
                    >
                      There are no certificate jobs
                      matching the current filters.
                    </Typography>

                  </td>

                </tr>

              ) : (

                filteredJobs.map((job) => {

                  const status =
                    String(
                      job?.status || ""
                    ).toLowerCase();

                  const statusConfig =
                    STATUS_CONFIG[status] ||
                    {
                      label:
                        job?.status ||
                        "Unknown",
                      color:
                        "default",
                    };


                  return (

                    <tr
                      key={getJobId(job)}
                    >

                      <td>
                        <Typography
                          fontWeight={700}
                          fontSize={13}
                        >
                          {getJobId(job)}
                        </Typography>
                      </td>


                      <td>
                        {getVehicleNumber(job)}
                      </td>


                      <td>
                        {getAssignedAgent(job)}
                      </td>


                      <td>
                        {
                          job?.manufacturer_name ||
                          job?.manufacturer ||
                          "-"
                        }
                      </td>


                      <td>
                        {
                          job?.protocol_format ||
                          "-"
                        }
                      </td>


                      <td>
                        {
                          job?.state_name ||
                          "-"
                        }
                      </td>


                      <td>
                        {
                          job?.district_name ||
                          "-"
                        }
                      </td>


                      <td>
                        {formatDate(
                          job?.submitted_at
                        )}
                      </td>


                      <td>

                        <Chip
                          size="small"
                          label={
                            statusConfig.label
                          }
                          color={
                            statusConfig.color
                          }
                        />

                      </td>


                      <td>
                        {renderActions(job)}
                      </td>

                    </tr>

                  );

                })

              )}

            </tbody>

          </Box>

        </Box>

      </Paper>


      {/* =================================================
          COMMON DIALOG
      ================================================= */}

      <Dialog
        open={dialog.open}
        onClose={
          loading
            ? undefined
            : closeDialog
        }
        fullWidth
        maxWidth={
          dialog.type === "details"
            ? "md"
            : "sm"
        }
      >

        {renderDialogContent()}

      </Dialog>


      {/* =================================================
          SNACKBAR
      ================================================= */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={closeSnackbar}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >

        <Alert
          severity={snackbar.severity}
          onClose={closeSnackbar}
          variant="filled"
          sx={{
            minWidth: 320,
          }}
        >
          {snackbar.message}
        </Alert>

      </Snackbar>

    </Box>
  );
};


// =====================================================
// DETAIL ITEM
// =====================================================

const DetailItem = ({
  label,
  value,
}) => {

  return (
    <Box>

      <Typography
        variant="caption"
        color="text.secondary"
        fontWeight={600}
      >
        {label}
      </Typography>

      <Typography
        variant="body2"
        fontWeight={600}
        sx={{
          mt: 0.3,
          wordBreak: "break-word",
        }}
      >
        {value || "-"}
      </Typography>

    </Box>
  );
};


export default CertificateReviewQueue;